import { useAppSelector } from '@/state/hooks';
import { PairMap, selectFavouriteMarketPairs, selectMarketPairs } from '@/state/reducers/marketPairSlice';
import { CSSProperties, MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { MarketListFilter, MarketListProps } from '@/components/modules/MarketList';
import UserMarket from '@/components/modules/MarketList/UserMarket';
import TraderUserMarket from '@/components/trader-modules/MarketList/TraderUserMarket';
import { selectAccountType } from '@/state/reducers/authSlice';
import { selectInstrumentConfigs } from '@/state/reducers/ohoSlice';
import { getInstrumentConfig } from '@/utils/hooks/useInstrument';
import useLanguage from '@/utils/hooks/useLanguage';
import { memoize } from '@mantine/core';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList } from 'react-window';
import { useDidUpdate } from './useDidUpdate';

interface UseMarketListParams extends MarketListProps {
    parentSize?: number;
}

export interface UseMarketListReturn {
    // Define the return types of the hook here
    userFavouriteMarketsCeler: string[];
    marketListFilters: { label: string; value: MarketListFilter }[];
    marketListFilter: MarketListFilter;
    setMarketListFilter: (value: MarketListFilter) => void;
    setFilter: (value: string) => void;
    hasInstruments: {
        filteredUserMarkets: PairMap[];
        hasFxInstruments: boolean;
        hasCryptoInstruments: boolean;
        hasIndexInstruments: boolean;
        hasCommInstruments: boolean;
        hasFuturesInstruments: boolean;
    };
    MarketList?: any;
}

const useMarketList = (params: UseMarketListParams): UseMarketListReturn => {
    const { parentSize = 0, isTraderMode, onClick } = params;

    const lang = useLanguage();
    const userMarkets = useAppSelector(selectMarketPairs);
    const userFavouriteMarkets = useAppSelector(selectFavouriteMarketPairs);
    const accountType = useAppSelector(selectAccountType);
    const instrumentConfigs = useAppSelector(selectInstrumentConfigs);

    const marketsWithConfig = useMemo(() => {
        return userMarkets.map((market) => {
            const config = getInstrumentConfig(accountType, instrumentConfigs, market.celer);
            return {
                ...market,
                config
            };
        });
    }, [userMarkets, accountType, instrumentConfigs]);

    const favouritesWithConfig = useMemo(() => {
        return userFavouriteMarkets.map((market) => {
            const config = getInstrumentConfig(accountType, instrumentConfigs, market.celer);
            return {
                ...market,
                config
            };
        });
    }, [userFavouriteMarkets, accountType, instrumentConfigs]);

    const [filter, setFilter] = useState('');
    const [marketListFilter, setMarketListFilter] = useState(MarketListFilter.All);

    const marketListFilters: { label: string; value: MarketListFilter }[] = useMemo(() => {
        return [
            { label: lang.common.showAll, value: MarketListFilter.All },
            { label: lang.common.fxSpot, value: MarketListFilter.FX },
            { label: lang.common.crypto, value: MarketListFilter.Crypto },
            { label: lang.common.indexCFD, value: MarketListFilter['Index CFD'] },
            { label: lang.common.commCFD, value: MarketListFilter['Comm CFD'] },
            { label: lang.common.futuresCFD, value: MarketListFilter['Futures CFD'] }
        ];
    }, [lang]);

    const userFavouriteMarketsCeler = useMemo(() => {
        return userFavouriteMarkets.map((market) => market.celer);
    }, [userFavouriteMarkets]);

    const sortedFavouriteMarkets = useMemo(() => {
        const temp = [...favouritesWithConfig];
        const sorted = sortMarkets(temp);
        return sorted;
    }, [favouritesWithConfig]);

    const sortedUserMarkets = useMemo(() => {
        const temp = marketsWithConfig.filter((market) => !userFavouriteMarketsCeler.includes(market.celer));
        const sorted = sortMarkets(temp);
        return [...sortedFavouriteMarkets, ...sorted];
    }, [marketsWithConfig, userFavouriteMarketsCeler, instrumentConfigs, accountType]);

    const hasInstruments = useMemo(() => {
        const temp = filter.replace('/', '').toUpperCase();
        let returnValues = sortedUserMarkets;
        const fxInstruments = returnValues.filter((market) => market.config?.type === 'FX');
        const cryptoInstruments = returnValues.filter((market) => market.config?.type === 'Crypto');
        const indexInstruments = returnValues.filter((market) => market.config?.type === 'Index');
        const commInstruments = returnValues.filter((market) => market.config?.type === 'Commodity');
        const futuresInstruments = returnValues.filter((market) => market.config?.type === 'Future');

        const hasFxInstruments = fxInstruments.length > 0;
        const hasCryptoInstruments = cryptoInstruments.length > 0;
        const hasIndexInstruments = indexInstruments.length > 0;
        const hasCommInstruments = commInstruments.length > 0;
        const hasFuturesInstruments = futuresInstruments.length > 0;

        if (marketListFilter === MarketListFilter.FX) returnValues = fxInstruments;
        else if (marketListFilter === MarketListFilter.Crypto) returnValues = cryptoInstruments;
        else if (marketListFilter === MarketListFilter['Index CFD']) returnValues = indexInstruments;
        else if (marketListFilter === MarketListFilter['Comm CFD']) returnValues = commInstruments;
        else if (marketListFilter === MarketListFilter['Futures CFD']) returnValues = futuresInstruments;
        return {
            filteredUserMarkets: [
                {
                    celer: 'header',
                    netdania: 'header',
                    show: 'header'
                } as PairMap,
                ...returnValues.filter((market) => {
                    return (
                        market.show?.toUpperCase().replace('/', '')?.includes(temp) ||
                        market.config?.description?.toUpperCase().includes(temp)
                    );
                })
            ],
            hasFxInstruments,
            hasCryptoInstruments,
            hasIndexInstruments,
            hasCommInstruments,
            hasFuturesInstruments
        };
    }, [sortedUserMarkets, marketListFilter, filter]);

    const listRef = useRef({}) as VariableSizeList;
    const rowHeights = useRef({});

    const getRowHeight = useCallback((index) => (index === 0 ? 20 : rowHeights.current[index] || 32), [rowHeights]);
    const setRowHeight = useCallback(
        (index, size) => {
            listRef.current.resetAfterIndex(0);
            // reset all rowHeights
            Object.keys(rowHeights.current).forEach((key) => {
                rowHeights.current[parseInt(key)] = 32;
            });
            rowHeights.current = { ...rowHeights.current, [index]: size };
        },
        [listRef, rowHeights]
    );

    const UserMarketRow = useCallback(
        ({ data, index, style }: ListRowItemProps) => {
            const { items, width } = data;
            const market = items[index];

            return (
                <UserMarket
                    style={{
                        ...style,
                        width: width <= 465 ? 'auto' : style.width
                    }}
                    market={market}
                    onClick={onClick}
                    parentSize={parentSize}
                />
            );
        },
        [onClick, parentSize]
    );

    const TraderUserMarketRow = useCallback(
        ({ data, index, style }: ListRowItemProps) => {
            const rowRef = useRef({}) as MutableRefObject<HTMLDivElement>;
            const { items, width } = data;

            const market = items[index];
            const rowHeight = getRowHeight(index);

            useEffect(() => {
                if (rowRef.current && rowHeight !== 61) setRowHeight(index, 32);
            }, [rowRef]);

            return (
                <TraderUserMarket
                    key={`${market.celer}-${index}`}
                    ref={rowRef}
                    style={{
                        ...style,
                        width: width <= 465 ? 'auto' : style.width
                    }}
                    market={market}
                    onClick={onClick}
                    onActive={() => {
                        if (rowHeight === 32) setRowHeight(index, 61);
                        else setRowHeight(index, 32);
                    }}
                    isActive={rowRef.current && rowHeight === 61}
                    parentSize={parentSize}
                />
            );
        },
        [parentSize, getRowHeight, setRowHeight, onClick]
    );

    const getItemSize = useCallback((index: number) => (index === 0 ? 20 : 32), []);
    const itemData = useMemo(
        () => createItemData(hasInstruments.filteredUserMarkets),
        [hasInstruments.filteredUserMarkets]
    );
    const VirtualizedList = useCallback(() => {
        if (isTraderMode) {
            return (
                <AutoSizer>
                    {({ height, width }) => (
                        <VariableSizeList
                            height={height}
                            width={width}
                            itemCount={itemData.items.length}
                            itemData={{
                                items: itemData.items,
                                width
                            }}
                            itemSize={getRowHeight}
                            ref={listRef}>
                            {TraderUserMarketRow}
                        </VariableSizeList>
                    )}
                </AutoSizer>
            );
        } else {
            return (
                <AutoSizer>
                    {({ height, width }) => (
                        <VariableSizeList
                            height={height}
                            width={width}
                            itemCount={itemData.items.length}
                            itemData={{
                                items: itemData.items,
                                width
                            }}
                            itemSize={getItemSize}>
                            {UserMarketRow}
                        </VariableSizeList>
                    )}
                </AutoSizer>
            );
        }
    }, [itemData, parentSize, isTraderMode]);

    // const TableMarketList = useCallback(() => {
    //     return <MarketListTable data={itemData.items} />;
    // }, [itemData, isTraderMode]);

    // Instrument, Description (jwt), Type (jwt), CFI (jwt), UPI (jwt), Bid, Spread, Ask

    useDidUpdate(() => {
        rowHeights.current = {};
    }, [userFavouriteMarketsCeler]);

    return {
        userFavouriteMarketsCeler,
        marketListFilters,
        marketListFilter,
        setMarketListFilter,
        setFilter,
        hasInstruments,
        MarketList: VirtualizedList
        // MarketList: parentSize < 600 ? VirtualizedList : TableMarketList
    };
};

export default useMarketList;

export interface ListItemProps {
    index: number;
    style: CSSProperties;
}

interface ListRowItemProps extends ListItemProps {
    data: { items: PairMap[]; width: number };
}

const createItemData = memoize((items) => ({ items }));

const sortMarkets = (markets: PairMap[]) => {
    return markets.sort((a, b) => {
        let compareA = a.show;
        let compareB = b.show;

        if (a.config?.type === 'Future') compareA = a.config.gcexSymbol;
        if (b.config?.type === 'Future') compareB = b.config.gcexSymbol;

        if (compareA < compareB) return -1;
        if (compareA > compareB) return 1;
        return 0;
    });
};
